'use client';

/**
 * NotificationItem Component
 *
 * A single notification row with Apple Liquid Glass styling.
 * Shows icon, title, message, timestamp, and read/unread state.
 * Supports swipe-to-archive on mobile and hover actions on desktop.
 *
 * @see Issue #699 - Notification System Fidelity
 * @see Apple WWDC 2025 - Liquid Glass design language
 */

import ArchiveIcon from '@mui/icons-material/Archive';
import AgentIcon from '@mui/icons-material/AutoAwesome';
import BillingIcon from '@mui/icons-material/CreditCard';
import DeleteIcon from '@mui/icons-material/Delete';
import DocumentIcon from '@mui/icons-material/Description';
import ReadIcon from '@mui/icons-material/DoneAll';
import IntegrationIcon from '@mui/icons-material/Extension';
import InfoIcon from '@mui/icons-material/Info';
import UnreadIcon from '@mui/icons-material/MarkEmailUnread';
import UserIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import SystemIcon from '@mui/icons-material/Settings';
import Box from '@mui/material/Box';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { NotificationSeverity, NotificationType } from '@ops-matrix/shared-types';
import { AnimatePresence, motion } from 'framer-motion';
import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { baseColors } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { springPresets } from '../../theme/transitions';
import { iconSizes } from '../../theme/typography';
import { GlassIconButton } from '../GlassIconButton';

// ============================================================================
// Types
// ============================================================================

export interface NotificationItemData {
  id: string;
  title: string;
  message: string;
  type: NotificationType | string;
  severity: NotificationSeverity | string;
  isRead: boolean;
  createdAt: Date | string;
  actionUrl?: string;
  imageUrl?: string;
  /**
   * True if the current user was @-mentioned in the notification source.
   * Optional — the backend may not set it on every notification yet. The
   * NotificationDropdown's `Mentions` filter pill renders an empty list if
   * no notification has this flag set. (#1758 v3 design, follow-up to #699.)
   */
  isMention?: boolean;
}

export interface NotificationItemProps {
  /** Notification data */
  notification: NotificationItemData;

  /** Called when the item is clicked */
  onClick?: (notification: NotificationItemData) => void;

  /** Called when mark as read/unread is triggered */
  onToggleRead?: (notification: NotificationItemData) => void;

  /** Called when archive is triggered */
  onArchive?: (notification: NotificationItemData) => void;

  /** Called when delete is triggered */
  onDelete?: (notification: NotificationItemData) => void;

  /** Compact mode for dropdown (less padding) */
  compact?: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

const typeIconMap: Record<string, React.ReactElement> = {
  system: <SystemIcon fontSize="small" />,
  document: <DocumentIcon fontSize="small" />,
  agent: <AgentIcon fontSize="small" />,
  billing: <BillingIcon fontSize="small" />,
  security: <SecurityIcon fontSize="small" />,
  user: <UserIcon fontSize="small" />,
  integration: <IntegrationIcon fontSize="small" />,
  custom: <InfoIcon fontSize="small" />,
};

const severityColorMap: Record<string, string> = {
  info: baseColors.info[500],
  warning: baseColors.warning[500],
  critical: baseColors.error[500],
  success: baseColors.success[500],
};

function getRelativeTime(date: Date | string, t: (key: string, params?: Record<string, string>) => string): string {
  const now = new Date();
  const d = typeof date === 'string' ? new Date(date) : date;
  const diffMs = now.getTime() - d.getTime();
  if (Number.isNaN(diffMs)) return t('notification.item.justNow');
  const diffMin = Math.floor(diffMs / 60000);
  const diffHour = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  const diffWeek = Math.floor(diffMs / 604800000);

  if (diffMin < 1) return t('notification.item.justNow');
  if (diffMin < 60) return t('notification.item.minutesAgo', { count: String(diffMin) });
  if (diffHour < 24) return t('notification.item.hoursAgo', { count: String(diffHour) });
  if (diffDay < 7) return t('notification.item.daysAgo', { count: String(diffDay) });
  return t('notification.item.weeksAgo', { count: String(diffWeek) });
}

// ============================================================================
// Component
// ============================================================================

export const NotificationItem = forwardRef<HTMLDivElement, NotificationItemProps>(
  ({ notification, onClick, onToggleRead, onArchive, onDelete, compact = false }, ref) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const { t } = useI18n();
    const [isHovered, setIsHovered] = useState(false);
    const glass = isDark ? glassEffects.dark : glassEffects.light;

    const icon = useMemo(() => typeIconMap[notification.type] ?? <InfoIcon fontSize="small" />, [notification.type]);
    const accentColor = useMemo(
      () => severityColorMap[notification.severity] ?? baseColors.info[500],
      [notification.severity],
    );
    const timeAgo = useMemo(() => getRelativeTime(notification.createdAt, t), [notification.createdAt, t]);

    const handleClick = useCallback(() => {
      onClick?.(notification);
    }, [onClick, notification]);

    const handleToggleRead = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onToggleRead?.(notification);
      },
      [onToggleRead, notification],
    );

    const handleArchive = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onArchive?.(notification);
      },
      [onArchive, notification],
    );

    const handleDelete = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onDelete?.(notification);
      },
      [onDelete, notification],
    );

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, x: -40, height: 0 }}
        transition={springPresets.snappy}
        layout
      >
        <Box
          onClick={handleClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          role="button"
          tabIndex={0}
          data-testid="notification-item"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick();
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: compact ? 1.5 : 2,
            px: compact ? 2 : 2.5,
            py: compact ? 1.5 : 2,
            cursor: 'pointer',
            position: 'relative',
            borderRadius: 2,
            transition: 'all 0.2s cubic-bezier(0.215, 0.61, 0.355, 1)',
            // Unread indicator - left accent bar (2px solid, full-height per v3 design)
            '&::before': !notification.isRead
              ? {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 2,
                  background: accentColor,
                }
              : undefined,
            // Background
            bgcolor: notification.isRead ? 'transparent' : isDark ? alpha(accentColor, 0.04) : alpha(accentColor, 0.03),
            // Hover
            '&:hover': {
              bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
            },
          }}
        >
          {/* Type icon with accent */}
          <Box
            sx={{
              width: compact ? 32 : 36,
              height: compact ? 32 : 36,
              borderRadius: compact ? 1.5 : 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              bgcolor: alpha(accentColor, isDark ? 0.12 : 0.08),
              color: accentColor,
              mt: 0.25,
            }}
          >
            {icon}
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.25 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: notification.isRead ? 500 : 600,
                  color: 'text.primary',
                  lineHeight: 1.4,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}
              >
                {notification.title}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.disabled',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  fontSize: '0.75rem',
                }}
              >
                {timeAgo}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: '-webkit-box',
                WebkitLineClamp: compact ? 1 : 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.5,
              }}
            >
              {notification.message}
            </Typography>
          </Box>

          {/* Hover actions */}
          <AnimatePresence>
            {isHovered && (onToggleRead || onArchive || onDelete) && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.15 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  position: 'absolute',
                  right: compact ? 8 : 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: glass.background,
                  backdropFilter: 'blur(20px)',
                  borderRadius: 8,
                  padding: '2px 4px',
                  border: `0.5px solid ${glass.border}`,
                  boxShadow: glass.shadow,
                }}
              >
                {onToggleRead && (
                  <GlassIconButton
                    size="xs"
                    variant="standard"
                    tooltip={notification.isRead ? t('notification.item.markUnread') : t('notification.item.markRead')}
                    onClick={handleToggleRead}
                  >
                    {notification.isRead ? (
                      <UnreadIcon sx={{ fontSize: iconSizes.xs }} />
                    ) : (
                      <ReadIcon sx={{ fontSize: iconSizes.xs }} />
                    )}
                  </GlassIconButton>
                )}
                {onArchive && (
                  <GlassIconButton
                    size="xs"
                    variant="standard"
                    tooltip={t('notification.item.archive')}
                    onClick={handleArchive}
                  >
                    <ArchiveIcon sx={{ fontSize: iconSizes.xs }} />
                  </GlassIconButton>
                )}
                {onDelete && (
                  <GlassIconButton
                    size="xs"
                    variant="standard"
                    tooltip={t('notification.item.delete')}
                    onClick={handleDelete}
                  >
                    <DeleteIcon sx={{ fontSize: iconSizes.xs }} />
                  </GlassIconButton>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </motion.div>
    );
  },
);

NotificationItem.displayName = 'NotificationItem';

export default NotificationItem;
